import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FirstPencilCreatingOverlay } from '@makemydeal/dr-dash-components';
import { SlideOutWrapper } from './SlideOutWrapper';
import { FirstPencilProgramsCompactUI } from '@makemydeal/dr-activities-programs';
import * as firstPencilActionCreators from '@makemydeal/dr-dash-store/actions/firstPencilActionCreators';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { FULL_SCREEN_SLIDE_OUT_WIDTH } from '@makemydeal/dr-shared-ui-utils';
import type { SlideOutComponentProps } from '../../types';

export const FIRST_PENCIL_APPLY_START_TIMEOUT_MS = 5000;
export const FIRST_PENCIL_APPLY_CALCULATION_TIMEOUT_MS = 30000;

export const FirstPencilIncentivesSlideOut = ({ isOpen, onClose }: SlideOutComponentProps) => {
    const dispatch = useDispatch();
    const selectedVehicle = useSelector(firstPencilSelectors.getSelectedVehicle);
    const selectedProgram = useSelector(firstPencilSelectors.getSelectedProgram);
    const hasDuplicateManualIncentiveCodes = useSelector(firstPencilSelectors.hasDuplicateManualIncentiveCodes);
    const hasInvalidIncentiveOverrides = useSelector(firstPencilSelectors.hasInvalidIncentiveOverrides);
    const isCalculatingPayment = useSelector(offerReduxSelectors.isCalculatingPayment);

    const [isApplying, setIsApplying] = useState(false);
    const wasCalculatingRef = useRef(false);

    const finishApplying = useCallback(() => {
        wasCalculatingRef.current = false;
        setIsApplying(false);
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (!isApplying) {
            return undefined;
        }
        if (isCalculatingPayment) {
            wasCalculatingRef.current = true;
            const calculationTimeout = setTimeout(finishApplying, FIRST_PENCIL_APPLY_CALCULATION_TIMEOUT_MS);
            return () => clearTimeout(calculationTimeout);
        }
        if (wasCalculatingRef.current) {
            finishApplying();
            return undefined;
        }
        const startTimeout = setTimeout(finishApplying, FIRST_PENCIL_APPLY_START_TIMEOUT_MS);
        return () => clearTimeout(startTimeout);
    }, [isCalculatingPayment, isApplying, finishApplying]);

    const handleBack = useCallback(() => {
        if (isApplying) {
            return;
        }
        dispatch(firstPencilActionCreators.programsBack());
        onClose({ cancelled: true });
    }, [dispatch, onClose, isApplying]);

    const handleCancel = useCallback(() => {
        if (isApplying) {
            return;
        }
        dispatch(firstPencilActionCreators.cancelled());
        onClose({ cancelled: true });
    }, [dispatch, onClose, isApplying]);

    const handleNext = useCallback(() => {
        setIsApplying(true);
        try {
            dispatch(firstPencilActionCreators.createConfirmed());
        } catch {
            finishApplying();
        }
    }, [dispatch, finishApplying]);

    return (
        <>
            <SlideOutWrapper
                title={'Programs & Incentives'}
                panelWidth={FULL_SCREEN_SLIDE_OUT_WIDTH}
                isOpen={isOpen}
                onClose={handleCancel}
                onBack={handleBack}
                testId="tab-slideout-first-pencil-incentives"
                footer={{
                    layout: 'rightAligned',
                    options: {
                        action: handleCancel,
                        label: 'Cancel',
                        buttonStyle: 'tertiary',
                        disabled: isApplying,
                        'data-testid': 'first-pencil-incentives-cancel'
                    },
                    primary: {
                        action: handleNext,
                        label: 'Next',
                        disabled:
                            !selectedProgram || hasDuplicateManualIncentiveCodes || hasInvalidIncentiveOverrides || isApplying,
                        'data-testid': 'first-pencil-incentives-next'
                    }
                }}
                vehicleOverride={selectedVehicle}
            >
                {isOpen && <FirstPencilProgramsCompactUI />}
            </SlideOutWrapper>
            {isApplying && <FirstPencilCreatingOverlay />}
        </>
    );
};
